import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { AppHeader } from '@src/design-system/molecules/app-header';
import { AppSidebar } from '@src/design-system/molecules/app-sidebar';
import { SidebarInset, SidebarProvider } from '@src/design-system/sidebar';
import { UserHeaderMetadata } from '@src/shared/components/common/user-header-metadata';
import { AssistantPanel } from '@features/assistant/components/assistant-panel';

/** Real URLs are `/:organizationSlug/conversations…` (see MainRoutes). */
function isConversationsPath(pathname: string) {
  const segments = pathname.split('/').filter(Boolean);
  // [organizationSlug, 'conversations', conversationId?]
  return segments[1] === 'conversations';
}

/** While the inbox is open, nothing above the panes may scroll — not the
 * outlet, and not the document. `scrollIntoView` / focus scrolls walk past
 * `overflow-hidden` flex ancestors and move `document.scrollingElement`,
 * which is what tucks the Conversations title under the app header. */
function useLockDocumentScroll(locked: boolean) {
  useEffect(() => {
    if (!locked) return;
    const html = document.documentElement;
    const { body } = document;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    html.scrollTop = 0;
    body.scrollTop = 0;
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, [locked]);
}

export function NewMainLayout() {
  const { pathname } = useLocation();
  const isInbox = isConversationsPath(pathname);
  const [open, setOpen] = useState(() => !isInbox);
  useLockDocumentScroll(isInbox);

  // Collapse the app nav on entering the inbox so the panes have width, but
  // do not lock it — the member can expand it again from the header trigger.
  useEffect(() => {
    if (isInbox) setOpen(false);
  }, [isInbox]);

  return (
    <SidebarProvider
      open={open}
      onOpenChange={setOpen}
    >
      <UserHeaderMetadata />
      <AppSidebar />
      <SidebarInset>
        <AppHeader />
        {/* The app's scroll region: everything below the header, and nothing
            above it.

            This was `h-full`, which is 100% of SidebarInset — the FULL viewport
            height, in a column where the header has already spent part of it.
            The region was therefore a header's-height taller than the space it
            had, so its own bottom sat off-screen: the last strip of every page
            was unreachable even after scrolling to the end, and any page tall
            enough to scroll took the header's height with it.

            `flex-1 min-h-0` is the same intent stated in terms the column can
            answer: whatever is left after the header, and no more. `min-h-0`
            is what permits "and no more" — without it a flex item refuses to
            shrink below its content, and the overflow-y-auto below never has
            anything to do. */}
        <div className='flex min-h-0 flex-1 scale-100 overflow-hidden'>
          {/* Fill the region under the header with flex-1 — not h-full.
              h-full is 100% of SidebarInset (the full viewport), which is a
              header too tall once AppHeader has already taken space, and
              leaves an empty strip at the bottom of the inbox.

              Inbox panes own their own scroll; an `overflow-y-auto` here is an
              ancestor `scrollIntoView` / focus can yank (and used to yank the
              whole Conversations chrome under the header). Keep the outlet as a
              non-scrolling frame on that route; other pages still scroll. */}
          <div
            className={
              isInbox
                ? 'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden'
                : 'flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto'
            }
          >
            <Outlet />
          </div>
          <AssistantPanel />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
